import { getIntersectionRoot, scrollContainerMediaQuery } from '@theme/scroll-container';

class PogiUgcWall extends HTMLElement {
  connectedCallback() {
    if (this.cleanup) return;
    this.button = this.querySelector('.pogi-ugc__toggle');
    this.label = this.querySelector('[data-ugc-label]');
    this.grid = this.querySelector('.pogi-ugc__grid');
    this.clips = [...this.querySelectorAll('video.pogi-ugc__video')].map(video => ({ video, wanted: false, attempt: null, failed: false }));
    if (!this.clips.length || !this.button || !this.label || !this.grid) return;
    this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.mobile = window.matchMedia('(max-width: 990px)');
    this.userPaused ??= false;
    this.override = false;
    this.blocked = false;
    this.leaving = false;
    const removers = [];
    const listen = (target, event, fn, options) => {
      target?.addEventListener?.(event, fn, options);
      removers.push(() => target?.removeEventListener?.(event, fn, options));
    };
    this.cleanup = () => removers.forEach(remove => remove());
    this.button.hidden = false;
    listen(this.button, 'click', () => {
      this.userPaused = this.enabled();
      this.override = !this.userPaused;
      this.blocked = false;
      if (!this.userPaused) this.clips.forEach(clip => { clip.failed = false; });
      this.refresh();
    });
    for (const clip of this.clips) {
      listen(clip.video, 'play', () => {
        if (!clip.wanted || !this.enabled() || !this.isConnected || document.hidden || this.leaving) this.stop(clip);
      });
      listen(clip.video, 'pause', () => {
        // Unexpected browser suspension becomes retryable with the group control.
        // Safari may suspend media before the visibility/scroll callback runs.
        if (clip.video.paused && clip.wanted) {
          this.refresh();
          if (clip.wanted) { this.blocked = true; this.refresh(); }
        }
      });
      listen(clip.video, 'error', () => { clip.failed = true; this.stop(clip); this.sync(); });
    }
    listen(document, 'visibilitychange', () => this.refresh());
    listen(window, 'pagehide', () => { this.leaving = true; this.refresh(); });
    listen(window, 'pageshow', () => { this.leaving = false; this.refresh(); });
    const preferenceChanged = () => { this.override = false; this.refresh(); };
    listen(this.motion, 'change', preferenceChanged);
    listen(navigator.connection, 'change', preferenceChanged);
    listen(this.mobile, 'change', () => this.refresh());
    listen(scrollContainerMediaQuery, 'change', () => this.observe());
    listen(document, 'scroll', () => this.schedule(), true);
    listen(window, 'resize', () => this.schedule());
    this.observe();
  }

  enabled() {
    return !this.userPaused && !this.blocked && !this.clips.every(clip => clip.failed)
      && (this.override || (!this.motion.matches && !navigator.connection?.saveData));
  }

  schedule() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => { this.frame = null; this.refresh(); });
  }

  loadPoster(video) {
    if (!video.getAttribute('poster') && video.dataset.poster) video.poster = video.dataset.poster;
  }

  observe() {
    this.observer?.disconnect();
    this.observer = null;
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => {
        if (this.observer !== observer || !this.isConnected) return;
        entries.forEach(entry => { if (entry.isIntersecting) this.loadPoster(entry.target); });
        this.schedule();
      }, { root: getIntersectionRoot(), rootMargin: '300px 0px', threshold: [0, 0.2, 0.5, 0.8, 1] });
      this.observer = observer;
      this.clips.forEach(clip => observer.observe(clip.video));
    } else this.clips.forEach(clip => this.loadPoster(clip.video));
    this.refresh();
  }

  refresh() {
    if (!this.cleanup) return;
    const eligible = new Set();
    if (this.enabled() && !document.hidden && !this.leaving && this.isConnected) {
      const root = getIntersectionRoot()?.getBoundingClientRect();
      const grid = this.grid.getBoundingClientRect();
      const visible = this.clips.map(clip => {
        const box = clip.video.getBoundingClientRect();
        const w = Math.max(0, Math.min(box.right, innerWidth, root?.right ?? Infinity, grid.right)
          - Math.max(box.left, 0, root?.left ?? 0, grid.left));
        const h = Math.max(0, Math.min(box.bottom, innerHeight, root?.bottom ?? Infinity, grid.bottom)
          - Math.max(box.top, 0, root?.top ?? 0, grid.top));
        return { clip, ratio: box.width && box.height ? w * h / (box.width * box.height) : 0, across: box.width ? w / box.width : 0 };
      }).filter(item => !item.clip.failed && item.ratio >= 0.2 && (!this.mobile.matches || item.across >= 0.8));
      if (this.mobile.matches) {
        visible.sort((a, b) => b.ratio - a.ratio);
        if (visible[0]) eligible.add(visible[0].clip);
      } else visible.forEach(item => eligible.add(item.clip));
    }
    // Stop ineligible clips before starting the next mobile card.
    this.clips.forEach(clip => { if (!eligible.has(clip)) this.stop(clip); });
    this.clips.forEach(clip => { if (eligible.has(clip) && !clip.wanted) this.start(clip); });
    this.sync();
  }

  start(clip) {
    clip.wanted = true;
    const video = clip.video;
    this.loadPoster(video);
    if (!video.getAttribute('src')) video.src = video.dataset.src;
    video.muted = true;
    try {
      const attempt = video.play();
      clip.attempt = attempt;
      attempt?.catch(error => {
        if (clip.attempt !== attempt || !this.cleanup) return;
        if (error.name === 'NotAllowedError') this.blocked = true;
        else clip.failed = true;
        this.stop(clip);
        this.refresh();
      });
    } catch (_) {
      clip.failed = true;
      this.stop(clip);
    }
  }

  stop(clip) {
    clip.wanted = false;
    clip.attempt = null;
    if (!clip.video.paused) clip.video.pause();
  }

  sync() {
    const enabled = this.enabled();
    this.button.toggleAttribute('data-playing', enabled);
    const label = enabled ? this.button.dataset.pauseLabel : this.button.dataset.playLabel;
    this.label.textContent = label;
    this.button.title = label;
  }

  disconnectedCallback() {
    if (!this.cleanup) return;
    this.clips.forEach(clip => this.stop(clip));
    this.observer?.disconnect();
    this.observer = null;
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = null;
    this.cleanup();
    this.cleanup = null;
  }
}

if (!customElements.get('pogi-ugc-wall')) customElements.define('pogi-ugc-wall', PogiUgcWall);
